import { dehydrate } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import formatISO from "date-fns/formatISO";
import { GetServerSidePropsContext } from "next";
import { useRouter } from "next/router";
import { NextSeo } from "next-seo";
import { useEffect, useMemo, useRef, useState } from "react";
import Dropdown from "@/components/dropdownLegacy";
import Link from "@/components/link/Link";
import ArticleList from "@/components/sitemap/ArticleList";
import Breadcrumb, { LinkT } from "@/components/sitemap/Breadcrumb";
import Tabs from "@/components/Tabs";
import {
  SITEMAP_PAGE_VIEW,
  SITE_MAP_DATE_DAY_SELECTED,
  SITE_MAP_DATE_MONTH_SELECTED,
  SITE_MAP_DATE_YEAR_SELECTED,
  SITE_MAP_MODULE,
  SITE_MAP_VIEW_SELECTED,
} from "@/constants/amplitude";
import { PLAYER_NEWS_SLUG_BY_TYPE } from "@/constants/playerNews";
import {
  DEFAULT_MAX_MONTH,
  DEFAULT_MIN_MONTH,
  DEFAULT_YEAR,
} from "@/constants/sitemap";
import {
  GetArticlesByDateRangeQuery,
  GetPlayerNewsQueryQuery,
  ShortFormNewsTypeEnum,
} from "@/graphql/gql/graphql";
import { ArticleInfoT, ShortFormInfoT } from "@/types/sitemap";
import { getArticleInfoByDateRange, getShortForms } from "@/utils/api";
import {
  getDateRangeByDate,
  getDateRangeLastWeek,
  getDateRangeThisWeek,
  getDateRangeToday,
  getDateRangeYesterday,
} from "@/utils/date";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import logger from "@/utils/logger";
import { loadHeader } from "@/utils/ssr";
import styles from "./sitemap.module.scss";

interface SiteMapProps {
  menuPath: string;
  years?: number[];
  year?: number;
  months?: number[];
  month?: number;
  days?: number[];
  day?: number;
  articlesInfo?: ArticleInfoT[];
  shortFormsInfo?: ShortFormInfoT[];
  showDates?: boolean;
}

const availableTimes = ["", "today", "yesterday", "this-week", "last-week"];

const menuOptions = [
  "All Years",
  "Today",
  "Yesterday",
  "This Week",
  "Last Week",
];

const urlMap = menuOptions.reduce(
  (prev, next, index) => ({
    ...prev,
    [next]: availableTimes[index] ?? "",
  }),
  {} as Record<string, string>
);
const valueMap = Object.fromEntries(
  Object.entries(urlMap).map(([k, v]) => [v, k])
);

const SitemapPage = ({
  menuPath,
  years,
  year,
  months,
  month,
  days,
  day,
  articlesInfo,
  shortFormsInfo,
  showDates,
}: SiteMapProps) => {
  const menuItem =
    Object.keys(urlMap).find((key) => urlMap[key] === menuPath) ?? "All Years";
  const [selected, setSelected] = useState(menuItem);
  const router = useRouter();
  const { logAmplitudeEvent, setPage } = useAmplitude();
  const pageLoadRef = useRef(false);
  useEffect(() => {
    if (!pageLoadRef.current) {
      setPage("sitemap");
      logAmplitudeEvent(SITEMAP_PAGE_VIEW);
    }
    pageLoadRef.current = true;
  }, [logAmplitudeEvent, setPage]);

  const [mobileOption, setMobileOption] = useState(
    menuPath === "All Years" ? "" : menuPath
  );

  const mobileSelect = (value: string) => {
    logAmplitudeEvent(SITE_MAP_VIEW_SELECTED, {
      Module: SITE_MAP_MODULE,
      "Item Selected": valueMap[value],
    });
    router.push(value);
    setMobileOption(value);
  };
  const tabSelect = (value: string) => {
    logAmplitudeEvent(SITE_MAP_VIEW_SELECTED, {
      Module: SITE_MAP_MODULE,
      "Item Selected": value,
    });
    router.push(urlMap[value]);
    setSelected(value);
  };
  const links: LinkT[] | undefined = useMemo(() => {
    if (day && month && year) {
      return [
        { title: year.toString(), url: `/sitemap/${year}` },
        {
          title: new Date(year, month, 1).toLocaleString("default", {
            month: "long",
          }),
          url: `/sitemap/${year}/${month + 1}`,
        },
        { title: day.toString() },
      ];
    }
    if (month && year) {
      return [
        { title: year.toString(), url: `/sitemap/${year}` },
        {
          title: new Date(year, month, 1).toLocaleString("default", {
            month: "long",
          }),
        },
      ];
    }
    if (year) {
      return [{ title: year.toString() }];
    }
    return undefined;
  }, [day, month, year]);

  // Transform shortFormsInfo in the component each render
  const transformedShortFormsInfo: ShortFormInfoT[] = (
    shortFormsInfo || []
  ).map((entity) => {
    if (entity?.sport?.slug && entity?.newsType?.enum) {
      const newsTypeSlug =
        PLAYER_NEWS_SLUG_BY_TYPE[entity.newsType.enum as ShortFormNewsTypeEnum];

      return {
        ...entity,
        slug: `${entity.sport.slug}/player-news/${newsTypeSlug}/${entity.slug}`,
      };
    }
    return entity;
  });

  // Merge articles and transformed short forms for display
  const combinedArticlesInfo = [
    ...(articlesInfo || []),
    ...transformedShortFormsInfo,
  ].sort(
    (a, b) =>
      new Date(a?.lastPublishedAt || "").getTime() -
      new Date(b?.lastPublishedAt || "").getTime()
  );

  return (
    <div key={menuItem + month + day + year} className={styles.container}>
      <NextSeo title="Sitemap" />
      <div className={styles.upperContainer}>
        <h1 className={styles.heading}>Site Map</h1>
        <div className={styles.dropdown}>
          <Dropdown
            value={mobileOption}
            setValue={mobileSelect}
            options={[
              { label: "All Years", slug: "/sitemap", value: "" },
              { label: "Today", slug: "/sitemap/today", value: "today" },
              {
                label: "Yesterday",
                slug: "/sitemap/yesterday",
                value: "yesterday",
              },
              {
                label: "This Week",
                slug: "/sitemap/this-week",
                value: "this-week",
              },
              {
                label: "Last Week",
                slug: "/sitemap/last-week",
                value: "last-week",
              },
            ]}
            label="VIEW"
            aria-label="Select a time range"
          />
        </div>
      </div>
      <div className={styles.sitemapSearchArea}>
        <div className={styles.timeRangeTabs}>
          <Tabs
            dataTestId="sports-tabs"
            items={menuOptions}
            selected={selected}
            setSelected={tabSelect}
          />
        </div>
        {links ? <Breadcrumb links={links} /> : undefined}
        {years ? (
          <div className={styles.years}>
            {years.map((yr) => (
              <p key={yr} className={styles.year}>
                <Link
                  onClick={() =>
                    logAmplitudeEvent(SITE_MAP_DATE_YEAR_SELECTED, {
                      Module: SITE_MAP_MODULE,
                      "Item Selected": yr,
                    })
                  }
                  next
                  href={`/sitemap/${yr}`}
                >
                  {yr}
                </Link>
              </p>
            ))}
          </div>
        ) : undefined}
        {months ? (
          <div className={styles.months}>
            {months.map((mnth) => {
              const monthName = new Date(
                year ?? DEFAULT_YEAR,
                mnth,
                1
              ).toLocaleString("default", { month: "long" });
              return (
                <p key={mnth} className={styles.month}>
                  <Link
                    onClick={() =>
                      logAmplitudeEvent(SITE_MAP_DATE_MONTH_SELECTED, {
                        Module: SITE_MAP_MODULE,
                        "Item Selected": monthName,
                      })
                    }
                    next
                    href={`/sitemap/${year}/${mnth + 1}`}
                  >
                    {monthName}
                  </Link>
                </p>
              );
            })}
          </div>
        ) : undefined}
        {days ? (
          <div className={styles.days}>
            {days.map((dy) => (
              <p key={dy} className={styles.day}>
                <Link
                  onClick={() =>
                    logAmplitudeEvent(SITE_MAP_DATE_DAY_SELECTED, {
                      Module: SITE_MAP_MODULE,
                      "Item Selected": dy,
                    })
                  }
                  next
                  href={`/sitemap/${year}/${(month ?? DEFAULT_MIN_MONTH) + 1}/${dy}`}
                >
                  {dy}
                </Link>
              </p>
            ))}
          </div>
        ) : undefined}
        {combinedArticlesInfo && combinedArticlesInfo.length > 0 ? (
          <ArticleList
            articlesInfo={combinedArticlesInfo}
            showDates={showDates}
          />
        ) : undefined}
      </div>
    </div>
  );
};

export async function getServerSideProps({
  params,
}: GetServerSidePropsContext) {
  let showDates = false;
  const menuClient = await loadHeader();
  const key1 = params?.sitemap?.at(0);
  const key2 = params?.sitemap?.at(1);
  const key3 = params?.sitemap?.at(2);
  const currentYear = new Date().getFullYear();

  // Define response types
  type ArticlesResponse = GetArticlesByDateRangeQuery | undefined;
  type ShortFormsResponse = GetPlayerNewsQueryQuery | undefined;

  // No params given – show list of years
  if (!key1) {
    const years: number[] = [];
    for (let yearIndex = DEFAULT_YEAR; yearIndex <= currentYear; yearIndex++) {
      years.push(yearIndex);
    }
    return {
      props: {
        years,
        menuPath: "All Years",
        dehydratedState: dehydrate(menuClient),
      },
    };
  }

  // When key1 is one of the available times ("today", "yesterday", etc.)
  if (availableTimes.includes(key1)) {
    let articlesInfo: ArticleInfoT[] = [];
    let shortFormsInfo: ShortFormInfoT[] = [];
    const currentDate = new Date();
    let publishedWithin = {};
    if (key1 === "today") {
      publishedWithin = getDateRangeToday(currentDate);
    } else if (key1 === "yesterday") {
      publishedWithin = getDateRangeYesterday(currentDate);
    } else if (key1 === "this-week") {
      showDates = true;
      publishedWithin = getDateRangeThisWeek(currentDate);
    } else if (key1 === "last-week") {
      showDates = true;
      publishedWithin = getDateRangeLastWeek(currentDate);
    }

    const [articlesResult, shortFormsResult] = await Promise.allSettled([
      getArticleInfoByDateRange({ publishedWithin }),
      getShortForms({ filter: { publishedWithin } }),
    ]);

    if (articlesResult.status === "fulfilled") {
      articlesInfo = (articlesResult.value.getArticles?.articles ?? [])
        .map((art) => art?.entity ?? null)
        .filter(
          (entity): entity is ArticleInfoT =>
            entity !== null && entity !== undefined
        );
    } else {
      logger.error("Error fetching articles:", articlesResult.reason);
    }
    if (shortFormsResult.status === "fulfilled") {
      shortFormsInfo = (shortFormsResult.value.getShortForms?.shortForms ?? [])
        .map((sf) => sf?.entity ?? null)
        .filter((entity): entity is ShortFormInfoT => entity != null);
    } else {
      logger.error("Error fetching short forms:", shortFormsResult.reason);
    }
    return {
      props: {
        showDates,
        menuPath: key1,
        dehydratedState: dehydrate(menuClient),
        articlesInfo,
        shortFormsInfo,
      },
    };
  }

  // When key1 is a year (numeric)
  if (!Number.isNaN(key1)) {
    const year = Number.parseInt(key1 || "0", 10);
    if (year >= DEFAULT_YEAR && year <= currentYear) {
      if (!key2) {
        // Return list of months for that year
        const months: number[] = [];
        const maxMonth =
          year === currentYear ? new Date().getMonth() : DEFAULT_MAX_MONTH;
        const minMonth = year === DEFAULT_YEAR ? 4 : DEFAULT_MIN_MONTH;
        for (let monthIndex = minMonth; monthIndex <= maxMonth; monthIndex++) {
          months.push(monthIndex);
        }
        return {
          props: {
            menuPath: "All Years",
            dehydratedState: dehydrate(menuClient),
            year,
            months,
          },
        };
      }
      const regexMonth = /^([1-9]|1[012])$/;
      if (regexMonth.test(key2)) {
        const month = Number.parseInt(key2, 10) - 1;
        if (!key3) {
          // No day provided – return list of unique day numbers from both articles and short forms in that month
          const day = 1;
          const start = new Date(year, month, day);
          const end = new Date(start);
          end.setMonth(start.getMonth() + 1);
          end.setMinutes(start.getMinutes() - 1);

          const [articlesResult, shortFormsResult] = await Promise.allSettled([
            getArticleInfoByDateRange({
              publishedWithin: {
                start: formatISO(start),
                end: formatISO(end),
              },
            }),
            getShortForms({
              filter: {
                publishedWithin: {
                  start: formatISO(start),
                  end: formatISO(end),
                },
              },
            }),
          ]);
          let articlesDates: ArticlesResponse;
          let shortFormDates: ShortFormsResponse;
          if (articlesResult.status === "fulfilled") {
            articlesDates = articlesResult.value;
          } else {
            logger.error("Error fetching articles:", articlesResult.reason);
          }
          if (shortFormsResult.status === "fulfilled") {
            shortFormDates = shortFormsResult.value;
          } else {
            logger.error(
              "Error fetching short forms:",
              shortFormsResult.reason
            );
          }
          const daysListFromArticles =
            articlesDates?.getArticles?.articles
              .map(
                (article) =>
                  article?.entity.lastPublishedAt &&
                  parseISO(article?.entity.lastPublishedAt)
              )
              .filter((publishDay): publishDay is Date =>
                Boolean(publishDay)
              ) ?? [];
          const daysListFromShortForms =
            shortFormDates?.getShortForms?.shortForms
              .map(
                (shortForm) =>
                  shortForm?.entity.lastPublishedAt &&
                  parseISO(shortForm?.entity.lastPublishedAt)
              )
              .filter((publishDay): publishDay is Date =>
                Boolean(publishDay)
              ) ?? [];
          const combinedDaysList = [
            ...daysListFromArticles,
            ...daysListFromShortForms,
          ];
          const daySet = new Set(
            combinedDaysList
              .filter((d): d is Date => d instanceof Date)
              .map((d) => d.getDate())
          );
          const days = [...daySet].sort((a, b) => a - b);
          return {
            props: {
              menuPath: "All Years",
              dehydratedState: dehydrate(menuClient),
              month,
              year,
              days,
            },
          };
        }
        // When a day is provided
        const regexDay = /^([1-9]|[12]\d|3[01])$/;
        if (regexDay.test(key3)) {
          const day = Number.parseInt(key3, 10);
          const publishedWithin = getDateRangeByDate(year, month, day);
          const [articlesResult, shortFormsResult] = await Promise.allSettled([
            getArticleInfoByDateRange({ publishedWithin }),
            getShortForms({ filter: { publishedWithin } }),
          ]);
          let articles: ArticleInfoT[] = [];
          let shortForms: ShortFormInfoT[] = [];
          if (articlesResult.status === "fulfilled") {
            articles = (articlesResult.value.getArticles?.articles ?? [])
              .map((art) => art?.entity)
              .filter(
                (entity): entity is ArticleInfoT =>
                  entity !== null && entity !== undefined
              );
          } else {
            logger.error(
              "Error fetching articles for day:",
              articlesResult.reason
            );
          }
          if (shortFormsResult.status === "fulfilled") {
            shortForms = (
              shortFormsResult.value.getShortForms?.shortForms ?? []
            )
              .map((art) => art?.entity)
              .filter((entity) => entity != null);
          } else {
            logger.error(
              "Error fetching articles for day:",
              shortFormsResult.reason
            );
          }
          if ([...articles, ...shortForms].length <= 0) {
            return { notFound: true };
          }
          return {
            props: {
              year,
              day,
              month,
              articlesInfo: articles,
              shortFormsInfo: shortForms,
              menuPath: "All Years",
              showDates,
              dehydratedState: dehydrate(menuClient),
            },
          };
        }
      }
    }
  }
  return { notFound: true };
}

export default SitemapPage;
